import { AppUser } from 'Src/views/App/App';
import axios from 'Src/helpers/api/axiosInstance';
import { uploadImage, uploadSuperAdminImage } from 'Src/helpers/service/image';
import { uploadFileAllPath } from 'Src/helpers/service/file';
import { FormDefinition, FormElement, FormMediaRef } from 'Src/types/formSchema';

const joinUrl = (base: string, path: string): string => {
  const normalizedBase = String(base || '').replace(/\/+$/g, '');
  const normalizedPath = String(path || '').replace(/^\/+/, '');
  return normalizedBase ? `${normalizedBase}/${normalizedPath}` : `/${normalizedPath}`;
};

export const buildFormMediaUrl = (mediaId: string, token?: string): string => {
  const id = String(mediaId || '').trim();
  const signedToken = String(token || '').trim();
  if (!id || !signedToken) return '';
  const query = `key=${encodeURIComponent(id)}&token=${encodeURIComponent(signedToken)}`;
  return `${joinUrl(window.apiBase, 'i/form-media')}?${query}`;
};
export const normalizeFormMediaUrl = (url: string): string => String(url || '').trim();

export const resolveLegacyFormImageUrl = (value: string): string => {
  const imageRef = normalizeFormMediaUrl(value);
  if (!imageRef || /^(?:https?:|data:|blob:)/i.test(imageRef)) return imageRef;

  const imageBase = String(window.apiImg || '').replace(/\/+$/g, '');
  if (!imageBase) return imageRef;
  if (/^\/?images\//i.test(imageRef)) return `${imageBase}/${imageRef.replace(/^\/+/, '')}`;
  if (!imageRef.includes('/') && !imageRef.includes('\\')) {
    return `${imageBase}/images/${encodeURIComponent(imageRef)}`;
  }
  return imageRef;
};

export const toDownloadFormMediaUrl = (url: string): string => {
  const trimmedUrl = normalizeFormMediaUrl(url);
  if (!trimmedUrl || !trimmedUrl.includes('/i/form-media') || trimmedUrl.includes('download=')) return trimmedUrl;
  return `${trimmedUrl}${trimmedUrl.includes('?') ? '&' : '?'}download=1`;
};
const mediaRefFromResponse = (file: File, response: any): FormMediaRef => {
  const id = String(response?.key || response?.id || '');
  const url = String(response?.link || response?.url || response?.picUrl || '');
  if (!id || !url) throw new Error('Upload did not return a valid file reference.');
  return {
    id,
    name: file.name,
    size: file.size,
    mimeType: file.type || 'application/octet-stream',
    url,
    token: String(response?.token || '')
  };
};

const imageNeedsResize = (
  image: HTMLImageElement,
  file: File,
  maxWidth: number,
  maxHeight: number,
  maxSizeBytes: number
) => image.naturalWidth > maxWidth || image.naturalHeight > maxHeight || file.size > maxSizeBytes;

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read image file.'));
    };
    image.src = url;
  });

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Unable to resize image.'))), type, quality);
  });

const resizeImageFile = async (file: File): Promise<File> => {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml' || file.type === 'image/gif') return file;
  const maxWidth = 1600;
  const maxHeight = 1200;
  const maxSizeBytes = 1.5 * 1024 * 1024;
  const image = await loadImage(file);
  if (!imageNeedsResize(image, file, maxWidth, maxHeight, maxSizeBytes)) return file;

  const scale = Math.min(maxWidth / image.naturalWidth, maxHeight / image.naturalHeight, 1);
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return file;
  context.drawImage(image, 0, 0, width, height);

  const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  const blob = await canvasToBlob(canvas, outputType, outputType === 'image/jpeg' ? 0.82 : 0.92);
  if (blob.size >= file.size) return file;

  const extension = outputType === 'image/jpeg' ? 'jpg' : 'png';
  const name = file.name.replace(/\.[^.]+$/, '') || 'image';
  return new File([blob], `${name}.${extension}`, { type: outputType, lastModified: Date.now() });
};

export const stripUnsignedFormMediaKeys = (definition: FormDefinition): FormDefinition => ({
  ...definition,
  elements: (definition.elements || []).map(element => {
    if (!['IMAGE', 'ATTACHMENT'].includes(element.type) || !element.config?.mediaUrl || element.config.mediaToken) {
      return element;
    }

    const { mediaId, mediaKey, mediaToken, ...config } = element.config;
    return { ...element, config };
  })
});
export const uploadFormFiles = async (
  user: AppUser,
  files: File[],
  options: { maxFiles?: number; maxFileSizeBytes?: number; allowedMimeTypes?: string[] } = {}
): Promise<FormMediaRef[]> => {
  const maxFiles = options.maxFiles || 1;
  if (files.length > maxFiles) throw new Error(`Please select no more than ${maxFiles} file(s).`);

  for (const file of files) {
    if (options.maxFileSizeBytes && file.size > options.maxFileSizeBytes) {
      throw new Error(`${file.name} exceeds the maximum file size.`);
    }
    if (
      options.allowedMimeTypes?.length &&
      !options.allowedMimeTypes.includes(file.type || 'application/octet-stream')
    ) {
      throw new Error(`${file.name} has an unsupported file type.`);
    }
  }

  return Promise.all(
    files.map(async file => {
      const data = new FormData();
      data.append('file', file, file.name);
      const response = await axios.post(`${window.apiBase}/i/form-media`, data, {
        headers: { usid: user.roleSessionKey, 'Content-Type': 'multipart/form-data' }
      });
      return mediaRefFromResponse(file, response.data);
    })
  );
};

const FORM_MEDIA_PROXY_SAFE_LIMIT_BYTES = 900 * 1024;

const shouldUseFileServiceUpload = (file: File): boolean => file.size > FORM_MEDIA_PROXY_SAFE_LIMIT_BYTES;

const isPayloadTooLarge = (error: any): boolean => {
  const status = Number(error?.response?.status || error?.request?.status || error?.status || 0);
  const message = String(error?.message || error?.response?.data || '').toLowerCase();
  return (
    status === 413 ||
    message.includes('413') ||
    message.includes('request entity too large') ||
    message.includes('payload too large')
  );
};

const uploadViaFileService = async (user: AppUser, file: File): Promise<FormMediaRef> => {
  const data = new FormData();
  data.append('file', file, file.name.replace(/\s+/g, '_'));
  const response = await uploadFileAllPath(user, data);
  return mediaRefFromResponse(file, response);
};

export const uploadFormImage = async (user: AppUser, file: File): Promise<FormMediaRef> => {
  if (!file.type.startsWith('image/')) throw new Error('Please select an image file.');
  const resized = await resizeImageFile(file);
  const response = await uploadImage(user, resized);
  const id = String(response?.key || response?.id || '');
  const url = String(
    response?.link || response?.url || response?.picUrl || (id ? `${window.apiImg}/images/${id}` : '')
  );
  if (!id || !url) throw new Error('Image upload did not return a valid file reference.');
  return {
    id,
    name: resized.name,
    size: resized.size,
    mimeType: resized.type || 'image/jpeg',
    url,
    token: ''
  };
};

export const uploadFormFile = async (user: AppUser, file: File): Promise<FormMediaRef> =>
  uploadViaFileService(user, file);
export const uploadFormImageAsSuperAdmin = async (file: File): Promise<string> => {
  if (!file.type.startsWith('image/')) throw new Error('Please select an image file.');
  const response = await uploadSuperAdminImage(await resizeImageFile(file));
  const id = String(response?.key || response?.id || '');
  const url = String(
    response?.link || response?.url || response?.picUrl || (id ? `${window.apiImg}/images/${id}` : '')
  );
  if (!url) throw new Error('Image upload did not return a URL.');
  return url;
};

export const uploadFormFilesForElement = (user: AppUser, files: File[], element: FormElement) =>
  uploadFormFiles(user, files, {
    maxFiles: element.config?.maxFiles,
    maxFileSizeBytes: element.config?.maxFileSizeBytes,
    allowedMimeTypes: element.config?.allowedMimeTypes
  });
